import React, { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Prose } from '../PanelBlock';
import { LABOKLIN_BORDER, LABOKLIN_RED } from '../../../theme/laboklinTheme';
import type { RunPanels, SectionItem } from '../../../types/labConsistency';

/**
 * One row per datapoint, carrying its aggregate; the per-run detail behind a toggle.
 *
 * Five essays side by side is unreadable, and asking a veterinarian to diff five
 * paragraphs is how a consistency report goes unread. So the default view is
 * item-major: one row per diagnosis or per test, with how many runs carried it and its
 * mean rank, sorted so the items every run agreed on sit above a divider and the ones
 * that move collect below it. Two blocks rather than a colour hunt - above the line is
 * the answer, below it is the question.
 *
 * Expanding a row is where the clinical judgement happens: it shows what each run
 * actually wrote about that one item, which is the only place five variants need to be
 * read, and only for the item being questioned.
 */

interface DatapointRowsProps {
  items: SectionItem[];
  runs: RunPanels[];
  /** The prose one run wrote about one datapoint, or null if that run lacked it. */
  proseFor: (run: RunPanels, itemId: number) => string | null;
  /** Position of a datapoint within one run, where the section is ordered. */
  rankFor?: (run: RunPanels, itemId: number) => number | null;
  emptyMessage: string;
}

export const DatapointRows: React.FC<DatapointRowsProps> = ({
  items,
  runs,
  proseFor,
  rankFor,
  emptyMessage,
}) => {
  const [open, setOpen] = useState<number | null>(null);

  if (items.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 1 }}>{emptyMessage}</Typography>
    );
  }

  const total = runs.length;
  const stable = items.filter((i) => i.runs === total);
  const varying = items.filter((i) => i.runs < total);

  const row = (item: SectionItem, unstable: boolean) => {
    const expanded = open === item.id;
    return (
      <Box key={item.id} sx={{ borderTop: `1px solid ${LABOKLIN_BORDER}` }}>
        <Box
          onClick={() => setOpen(expanded ? null : item.id)}
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            py: 0.85,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
          }}
        >
          {expanded ? (
            <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
          ) : (
            <KeyboardArrowRightIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
          )}
          <Typography sx={{ fontSize: 13, flex: 1, minWidth: 0, fontWeight: unstable ? 400 : 500 }}>
            {item.name}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: unstable ? LABOKLIN_RED : 'text.secondary',
              fontWeight: unstable ? 600 : 400,
              whiteSpace: 'nowrap',
            }}
          >
            {item.runs}/{total} runs
            {item.meanRank !== null && ` · avg rank ${item.meanRank.toFixed(1)}`}
          </Typography>
        </Box>

        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ pl: 3, pb: 1.25 }}>
            {runs.map((run) => {
              const prose = proseFor(run, item.id);
              const rank = rankFor?.(run, item.id) ?? null;
              return (
                <Box key={run.run} sx={{ mb: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    Run {run.run}
                    {rank !== null && ` · rank ${rank}`}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.55,
                      color: prose === null ? 'text.disabled' : 'text.primary',
                      fontStyle: prose === null ? 'italic' : 'normal',
                    }}
                    component="div"
                  >
                    {/* A run that lacked the item is shown, not omitted: its absence is
                        the finding, and a gap in the list would hide it. */}
                    {prose === null ? (
                      'Not offered in this run'
                    ) : prose ? (
                      <Prose>{prose}</Prose>
                    ) : (
                      '(no summary written)'
                    )}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {stable.map((i) => row(i, false))}
      {varying.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 1.25,
            mb: 0.25,
            '&::after': { content: '""', flex: 1, borderTop: `1px dashed ${LABOKLIN_BORDER}` },
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: LABOKLIN_RED,
              fontWeight: 600,
            }}
          >
            Varies across runs
          </Typography>
        </Box>
      )}
      {varying.map((i) => row(i, true))}
    </Box>
  );
};

export default DatapointRows;
